import { el, clear } from '../../core/dom.js';
import { toast } from '../../components/toast.js';
import { empty } from '../../components/empty.js';
import { LANGUAGES, languageOptionLabel } from '../../core/languages.js';
import { getWorkspaceSettings, putWorkspaceSettings } from './actions.js';

/**
 * Settings → Workspace: preferred chat language (plan 1 §6.9.1 / S36a).
 * @param {{
 *   host: HTMLElement,
 *   me: object,
 *   isAdmin: boolean,
 *   dead: () => boolean,
 * }} opts
 */
export function mountWorkspace(opts) {
  const { host, me, isAdmin, dead } = opts;
  let busy = false;

  async function load() {
    clear(host);
    if (!isAdmin) {
      host.append(empty({
        title: 'Admin only',
        body: 'Only workspace admins can change preferred chat language.',
      }));
      return;
    }

    host.append(el('p', { class: 'set-hint' }, 'Loading…'));
    const data = await getWorkspaceSettings();
    if (dead()) return;
    clear(host);

    if (!data || data.error) {
      host.append(empty({
        title: 'Could not load',
        body: (data && data.error) || 'Try again in a moment.',
      }));
      return;
    }

    const current = data.chat_language || 'en';
    const languages = Array.isArray(data.languages) && data.languages.length
      ? data.languages
      : LANGUAGES;

    const select = el('select', {
      class: 'set-input',
      id: 'set-chat-language',
      'aria-label': 'Preferred chat language',
      disabled: busy ? true : null,
    },
    ...languages.map((lang) => el('option', {
      value: lang.code,
      selected: lang.code === current ? true : null,
    }, languageOptionLabel(lang))));

    const save = el('button', {
      type: 'button',
      class: 'set-cta',
      disabled: busy ? true : null,
      async onClick() {
        if (busy) return;
        busy = true;
        save.disabled = true;
        select.disabled = true;
        save.textContent = 'Saving…';
        const result = await putWorkspaceSettings({ chat_language: select.value });
        busy = false;
        if (dead()) return;
        save.disabled = false;
        select.disabled = false;
        save.textContent = 'Save';
        if (!result || result.error) {
          toast((result && result.error) || 'Could not save language', 'error');
          return;
        }
        toast('Preferred chat language saved');
      },
    }, 'Save');

    const tenantName = (me && me.tenant_name) || '—';
    const tenantSlug = (me && me.tenant_slug) || '—';

    host.append(
      el('div', { class: 'set-panel' },
        el('div', { class: 'set-panel-head' },
          el('div', { class: 'set-panel-title' }, el('span', {}, 'Preferred chat language'))),
        el('div', { class: 'set-byok-body' },
          el('p', {},
            'Bonnie defaults to this language for replies and ranking. English stays available; members can still write in any language.'),
          el('label', { class: 'set-field' },
            el('span', { class: 'lbl' }, 'Chat language'),
            select),
          el('div', { class: 'set-byok-row' }, save))),
      el('div', { class: 'set-panel' },
        el('div', { class: 'set-panel-head' },
          el('div', { class: 'set-panel-title' }, el('span', {}, 'Organization'))),
        el('div', { class: 'set-byok-body' },
          el('p', { class: 'set-hint' }, `${tenantName} · ${tenantSlug}`))),
    );
  }

  load();
  return { dispose() {} };
}
